import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  AllPlanScheduleType,
  MealType,
  RichContext,
  RoutineActivity,
  ScheduledAction,
  TaskItemForSchedule,
} from '@demind-inc/core';

import {
  suggestAllAIPlanStateAtom,
  isAllPlanPanelOpenAtom,
  sidebarOpenAtom,
  eventsSnackBarAtom,
  useAuthContext,
  useCalendarContext,
  useCalendarEventsCache,
  useEventActions,
  filterVisibleEvents,
  getMealTypeFromName,
  visibleCalendarIdsAtom,
  getPersistedDailyPlanJob,
  useSuggestAllAIPlan,
  usePreferences,
} from '../data-access';
import type { UnifiedChatRichContext } from '../data-access';
import { useEditMeal } from './useEditMeal';
import { useEditSleep } from './useEditSleep';
import { extractMealEditorDefaultsFromRichContext } from '../components/AiAssistantChat/RichContext/mealSignalUtils';
import {
  resolveScheduleType,
  getScheduleId,
} from '../components/Scheduler/AllPlanScheduler/helpers';
import { translateAllPlanScheduledActionToCalendarEvent } from '../helpers';
import {
  buildPlanCacheEntryFromSnapshot,
  buildPlanCacheSnapshotForDate,
  findPendingPlanCacheJob,
  isAllPlanScheduleType,
  persistDailyDefaultsPlanCacheForDate,
  persistPlanCacheForDate,
  resolveAllPlanAutoRunDecision,
  resolveAllPlanCacheDateKey,
  shouldPersistPlanCacheSnapshot,
  shouldUpdateTodayScopedPlanState,
  updatePlanCacheForDate,
} from '../helpers/allPlanCache.helpers';
import {
  applyAllPlanHomeRestoreIfNeeded,
  getLastAutoRunKey,
  setLastAutoRunKey,
  shouldSkipAutoRunForActiveFuturePlan,
} from '../helpers/allPlanHomeBootstrap.helpers';
import {
  computeTodayCalendarSignature,
  enrichSchedulesWithReplannedMetadata,
  findExistingEventForAllPlanSchedule,
} from '../helpers/allPlanScheduleMatch.helpers';
import {
  buildDailyDefaultsFocusAreas,
  buildDailyDefaultsToastMessage,
  filterLiveSchedulesAfterDailyDefaultsRerun,
  mealTypesForDailyDefaultKinds,
  partitionDailyDefaultsRerunSchedules,
  readDailyDefaultsStore,
  resolveDailyDefaultsTypesToRun,
  resolveExistingAnchorPlanTypes,
  writeDailyDefaultsStore,
} from '../helpers/dailyDefaultsPlan.helpers';
import { trackEventMixpanel } from '../utils';
import { useAssistantPref } from '../data-access/hooks/useAssistantPref';
import { cancelChatJob } from '../data-access/api/jobs';

const ALL_PLAN_SCHEDULE_TYPES: AllPlanScheduleType[] = [
  'meal',
  'sleep',
  'workout',
  'todo',
  'routine',
];

const ANCHOR_PLAN_TYPES: AllPlanScheduleType[] = ['meal', 'sleep', 'workout'];

type UseAllPlanSuggestedModalOptions = {
  /** When true, auto-run plan on the first visit of the day (e.g. onboarding preview). */
  autoRunOnFirstVisit?: boolean;
};

export const useAllPlanSuggestedModal = ({
  autoRunOnFirstVisit = false,
}: UseAllPlanSuggestedModalOptions = {}) => {
  const {
    skipAllPlanning,
    autoAcceptTypes,
    runSuggestAllAIPlan,
    suggestedSchedules: schedules,
    suggestedSummary: summary,
    statusLines,
    isLoading,
    error,
    resetAllPlanState,
    targetDate: plannedTargetDate,
  } = useSuggestAllAIPlan();

  const [suggestAllAIPlanState, setSuggestAllAIPlanState] =
    useRecoilState(suggestAllAIPlanStateAtom);
  const { acceptedIds, autoAcceptedIds, discardedIds } = suggestAllAIPlanState;
  const setIsAllPlanPanelOpen = useSetRecoilState(isAllPlanPanelOpenAtom);
  const setSidebarOpen = useSetRecoilState(sidebarOpenAtom);
  const { user } = useAuthContext();
  const { mainCalendar, calendarPreferences } = useCalendarContext();
  const { createCalendarEvent, updateCalendarEvent } = useEventActions();
  const setEventsSnackbar = useSetRecoilState(eventsSnackBarAtom);
  const { mealPref } = useAssistantPref();
  const { schedulerPreferences } = usePreferences({ preferenceId: user.preferenceId! });
  const { handleEditSleepPrediction } = useEditSleep({ date: dayjs(), skipAutoSuggestion: true });
  const { handleEditMealPrediction } = useEditMeal({ date: dayjs() });
  const visibleCalendarIds = useRecoilValue(visibleCalendarIdsAtom);
  const { findInCacheByDate } = useCalendarEventsCache();

  const today = dayjs().format('YYYY-MM-DD');
  const planDateKey = plannedTargetDate || today;
  const planDateNextKey = dayjs(planDateKey).add(1, 'day').format('YYYY-MM-DD');

  const [plannedTypes, setPlannedTypes] = useState<AllPlanScheduleType[]>([]);

  const autoRunAbortControllerRef = useRef<AbortController | null>(null);
  const rerunAbortControllerRef = useRef<AbortController | null>(null);
  const dailyDefaultsRunPromiseRef = useRef<Promise<void> | null>(null);
  const plannedTypesRef = useRef<AllPlanScheduleType[]>([]);
  const acceptedIdsRef = useRef<string[]>(acceptedIds);
  const discardedIdsRef = useRef<string[]>(discardedIds);
  const runSuggestAllAIPlanRef = useRef(runSuggestAllAIPlan);
  const setSuggestAllAIPlanStateRef = useRef(setSuggestAllAIPlanState);
  const isLoadingRef = useRef(isLoading);
  const plannedTargetDateRef = useRef(plannedTargetDate);
  const schedulesLengthRef = useRef(schedules.length);
  const schedulesRef = useRef(schedules);

  const cachedEventsForPlanDate = useMemo(() => {
    if (!user?.userId) return [];
    const planDayEvents = filterVisibleEvents(
      findInCacheByDate(planDateKey, user.userId) ?? [],
      visibleCalendarIds
    );
    const nextDayEvents = filterVisibleEvents(
      findInCacheByDate(planDateNextKey, user.userId) ?? [],
      visibleCalendarIds
    );
    const seen = new Set<string>();
    return [...planDayEvents, ...nextDayEvents].filter((e) => {
      if (seen.has(e.eventId)) return false;
      seen.add(e.eventId);
      return true;
    });
  }, [findInCacheByDate, planDateKey, planDateNextKey, user?.userId, visibleCalendarIds]);

  const cachedEventsRef = useRef(cachedEventsForPlanDate);

  const existingAnchorTypes = useMemo(
    () =>
      resolveExistingAnchorPlanTypes({
        planDateKey,
        calendarEvents: cachedEventsForPlanDate,
        pendingSchedules: schedules,
        discardedIds,
      }),
    [cachedEventsForPlanDate, discardedIds, planDateKey, schedules]
  );

  const skipTypesFromPlannedTypes = useMemo(() => {
    const skip = new Set<AllPlanScheduleType>();
    for (const type of ANCHOR_PLAN_TYPES) {
      if (plannedTypes.includes(type) || existingAnchorTypes.includes(type)) {
        skip.add(type);
      }
    }
    return Array.from(skip);
  }, [existingAnchorTypes, plannedTypes]);

  const defaultRerunTypes = useMemo(
    () => ALL_PLAN_SCHEDULE_TYPES.filter((t) => !skipTypesFromPlannedTypes.includes(t)),
    [skipTypesFromPlannedTypes]
  );
  // Open via isAllPlanPanelOpenAtom — not useAssistantChat — because this hook
  // is also called inside AssistantChatProvider itself (Plan My Day preview),
  // so consuming that context here would throw before the provider mounts.
  // AssistantChatProvider / SideBar already watch the atom and flip
  // setOpenAssistantChat(true) + ensure the plan preview bubble.
  const openAIChatSidebar = useCallback(() => {
    setSidebarOpen(true);
    setIsAllPlanPanelOpen(true);
  }, [setIsAllPlanPanelOpen, setSidebarOpen]);

  useEffect(() => {
    plannedTypesRef.current = plannedTypes;
  }, [plannedTypes]);
  useEffect(() => {
    cachedEventsRef.current = cachedEventsForPlanDate;
  }, [cachedEventsForPlanDate]);
  useEffect(() => {
    acceptedIdsRef.current = acceptedIds;
  }, [acceptedIds]);
  useEffect(() => {
    discardedIdsRef.current = discardedIds;
  }, [discardedIds]);
  useEffect(() => {
    runSuggestAllAIPlanRef.current = runSuggestAllAIPlan;
  }, [runSuggestAllAIPlan]);
  useEffect(() => {
    setSuggestAllAIPlanStateRef.current = setSuggestAllAIPlanState;
  }, [setSuggestAllAIPlanState]);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);
  useEffect(() => {
    plannedTargetDateRef.current = plannedTargetDate;
  }, [plannedTargetDate]);
  useEffect(() => {
    schedulesLengthRef.current = schedules.length;
    schedulesRef.current = schedules;
  }, [schedules]);

  useEffect(() => {
    if (!skipAllPlanning) return;
    setSuggestAllAIPlanState((prev) => ({
      ...prev,
      schedules: [],
      summary: null,
      acceptedIds: [],
      autoAcceptedIds: [],
      discardedIds: [],
      statusLines: [],
      isLoading: false,
      error: null,
    }));
  }, [setSuggestAllAIPlanState, skipAllPlanning]);

  const discardSchedule = useCallback(
    (id: string) => {
      trackEventMixpanel('discard_plan_item', { source: 'all_plan' });
      setSuggestAllAIPlanState((prev) => ({
        ...prev,
        discardedIds: prev.discardedIds.includes(id)
          ? prev.discardedIds
          : [...prev.discardedIds, id],
        acceptedIds: prev.acceptedIds.filter((x) => x !== id),
        autoAcceptedIds: prev.autoAcceptedIds.filter((x) => x !== id),
      }));
      void updatePlanCacheForDate(resolveAllPlanCacheDateKey(planDateKey), (existing) => {
        if (!existing) {
          return null;
        }
        return {
          ...existing,
          discardedIds: existing.discardedIds
            ? Array.from(new Set([...existing.discardedIds, id]))
            : [id],
          acceptedIds: existing.acceptedIds ? existing.acceptedIds.filter((x) => x !== id) : [],
          autoAcceptedIds: existing.autoAcceptedIds
            ? existing.autoAcceptedIds.filter((x) => x !== id)
            : [],
        };
      }).catch(() => undefined);
    },
    [planDateKey, setSuggestAllAIPlanState]
  );

  const updateScheduleTime = useCallback(
    (id: string, startDate: string, endDate: string) => {
      setSuggestAllAIPlanState((prev) => ({
        ...prev,
        schedules: prev.schedules.map((schedule, index) => {
          if (getScheduleId(schedule, index) !== id) {
            return schedule;
          }
          return {
            ...schedule,
            startDate,
            endDate,
          };
        }),
      }));

      void updatePlanCacheForDate(resolveAllPlanCacheDateKey(planDateKey), (existing) => {
        if (!existing || !Array.isArray(existing.schedules)) {
          return null;
        }

        const nextSchedules = existing.schedules.map((rawSchedule: unknown, index: number) => {
          const typedSchedule = rawSchedule as ScheduledAction;
          if (getScheduleId(typedSchedule, index) !== id) {
            return rawSchedule;
          }
          return {
            ...typedSchedule,
            startDate,
            endDate,
          };
        });

        return {
          ...existing,
          schedules: nextSchedules,
        };
      }).catch(() => undefined);
    },
    [planDateKey, setSuggestAllAIPlanState]
  );

  const acceptSchedule = useCallback(
    async (
      id: string,
      scheduleArg?: ScheduledAction,
      options?: { isAutoAccepted?: boolean; cacheDate?: string }
    ) => {
      if (!mainCalendar?.calendarId) {
        return;
      }

      const schedule =
        scheduleArg ??
        schedules.find((item, index) => {
          return getScheduleId(item, index) === id;
        });
      if (!schedule) {
        return;
      }
      const isAutoAccepted = options?.isAutoAccepted === true;
      // Prefer an explicit run target date so auto-accept decisions land on the
      // same cache entry as the schedules persist (stale planDateKey closure
      // otherwise writes today / nowhere and refresh rehydrates Lunch as pending).
      const cacheDateKey = resolveAllPlanCacheDateKey(options?.cacheDate ?? planDateKey);

      trackEventMixpanel('accept_all_plan_schedule', {
        scheduleType: resolveScheduleType(schedule),
      });

      // Preserve accepted/discarded status locally + in cache.
      setSuggestAllAIPlanState((prev) => {
        const nextAccepted = prev.acceptedIds.includes(id)
          ? prev.acceptedIds
          : [...prev.acceptedIds, id];
        const nextDiscarded = prev.discardedIds.filter((x) => x !== id);
        const nextAutoAccepted = isAutoAccepted
          ? prev.autoAcceptedIds.includes(id)
            ? prev.autoAcceptedIds
            : [...prev.autoAcceptedIds, id]
          : prev.autoAcceptedIds.filter((x) => x !== id);
        return {
          ...prev,
          acceptedIds: nextAccepted,
          autoAcceptedIds: nextAutoAccepted,
          discardedIds: nextDiscarded,
        };
      });
      try {
        await updatePlanCacheForDate(cacheDateKey, (existing) => {
          if (!existing) {
            // Persist ran under a different key or hasn't flushed — still store
            // the decision so refresh doesn't resurrect the preview.
            return {
              lastRunAtISO: new Date().toISOString(),
              signature: '',
              schedules: [schedule],
              summary: null,
              acceptedIds: [id],
              autoAcceptedIds: isAutoAccepted ? [id] : [],
              discardedIds: [],
            };
          }
          return {
            ...existing,
            acceptedIds: Array.isArray(existing.acceptedIds)
              ? Array.from(new Set([...existing.acceptedIds, id]))
              : [id],
            autoAcceptedIds: isAutoAccepted
              ? Array.isArray(existing.autoAcceptedIds)
                ? Array.from(new Set([...existing.autoAcceptedIds, id]))
                : [id]
              : Array.isArray(existing.autoAcceptedIds)
                ? existing.autoAcceptedIds.filter((x) => x !== id)
                : [],
            discardedIds: Array.isArray(existing.discardedIds)
              ? existing.discardedIds.filter((x) => x !== id)
              : [],
          };
        });
      } catch {
        // ignore cache failures
      }

      const timeZone = dayjs.tz.guess();
      const startISO = schedule.startDate;
      const endISO = schedule.endDate;

      const taskId = schedule.todoFrom?.taskId;
      const routineId = schedule.routineFrom?.routineActivityId;
      const scheduleType = resolveScheduleType(schedule);

      if (scheduleType === 'sleep') {
        if (user.metricId) {
          await handleEditSleepPrediction({
            editedManually: false,
            newData: {
              sleepTime: startISO,
              wakeTime: endISO,
            },
            shouldUpdateEvent: true,
            description: schedule.description || '',
            baseCalendarId: calendarPreferences?.defaultCalendarId || mainCalendar?.calendarId,
          });
          return;
        }
      }

      if (scheduleType === 'meal') {
        const mealType = getMealTypeFromName(schedule.name);
        if (user.metricId && mealType) {
          const richDefaults = extractMealEditorDefaultsFromRichContext(
            (schedule.richContext as UnifiedChatRichContext | null | undefined) ?? null,
            startISO,
            endISO,
            mealType
          );
          const normalizedTitle = (richDefaults.title || schedule.name || '').trim();
          await handleEditMealPrediction({
            newData: {
              mealType,
              startTime: startISO,
              endTime: endISO,
              ...(normalizedTitle ? { mealContent: normalizedTitle, title: normalizedTitle } : {}),
              ...(richDefaults.foodItems.length > 0 ? { foodItems: richDefaults.foodItems } : {}),
              ...(richDefaults.macros ? { macros: richDefaults.macros } : {}),
              ...(richDefaults.micros ? { micros: richDefaults.micros } : {}),
            },
            shouldUpdateEvent: true,
            description: richDefaults.description || schedule.description || '',
            baseCalendarId: calendarPreferences?.defaultCalendarId || mainCalendar?.calendarId,
          });
          return;
        }
      }

      const existingEvent = findExistingEventForAllPlanSchedule({
        calendarEvents: cachedEventsForPlanDate,
        taskId,
        routineId,
        scheduleType,
        scheduleStartDate: schedule.startDate,
        scheduleEndDate: schedule.endDate,
        scheduleName: schedule.name,
      });

      if (existingEvent?.eventId) {
        const nextScheduleFrom =
          scheduleType === 'sleep'
            ? 'sleep'
            : scheduleType === 'meal'
              ? 'meal'
              : scheduleType === 'workout'
                ? 'workout'
                : scheduleType === 'todo'
                  ? 'todo'
                  : scheduleType === 'routine'
                    ? 'routine'
                    : scheduleType === 'calendar'
                      ? 'calendar'
                      : existingEvent.scheduleFrom;

        await updateCalendarEvent({
          eventId: existingEvent.eventId,
          calendarId: existingEvent.calendarId || mainCalendar.calendarId,
          suppressEventSnackbar: isAutoAccepted,
          newEventOption: {
            ...existingEvent,
            scheduleFrom: nextScheduleFrom,
            richContext: {
              ...existingEvent.richContext,
              content: schedule.richContext?.content || existingEvent.richContext?.content || [],
            } as RichContext,
            summary: schedule.name,
            start: { date: startISO, timeZone },
            end: { date: endISO, timeZone },
          },
        });
      } else {
        const newEvent = translateAllPlanScheduledActionToCalendarEvent({
          ...schedule,
          startDate: startISO,
          endDate: endISO,
        });

        await createCalendarEvent({
          calendarId: mainCalendar.calendarId,
          suppressEventSnackbar: isAutoAccepted,
          newEventOption: {
            ...newEvent,
            start: { date: startISO, timeZone },
            end: { date: endISO, timeZone },
            showAs: calendarPreferences?.transparency?.aiScheduler,
            unSyncToCalendar: !!calendarPreferences?.unSyncToCalendar?.aiScheduler,
          },
        });
      }
    },
    [
      cachedEventsForPlanDate,
      calendarPreferences,
      createCalendarEvent,
      handleEditMealPrediction,
      handleEditSleepPrediction,
      mainCalendar,
      planDateKey,
      schedules,
      setSuggestAllAIPlanState,
      updateCalendarEvent,
      user.metricId,
    ]
  );

  const autoAcceptSchedules = useCallback(
    async (
      nextSchedules: ScheduledAction[],
      abortSignal?: AbortSignal,
      options?: {
        forceTypes?: AllPlanScheduleType[];
        commitMealTypes?: MealType[];
        /** Cache date for accept decisions — must match the schedule persist key. */
        targetDate?: string;
      }
    ) => {
      if ((!autoAcceptTypes && !options?.forceTypes?.length) || !mainCalendar?.calendarId) {
        return;
      }
      const forceTypes = new Set(options?.forceTypes ?? []);
      const commitMealTypes = options?.commitMealTypes ? new Set(options.commitMealTypes) : null;

      for (let index = 0; index < nextSchedules.length; index += 1) {
        if (abortSignal?.aborted) {
          return;
        }

        const schedule = nextSchedules[index];
        const id = getScheduleId(schedule, index);
        if (acceptedIdsRef.current.includes(id) || discardedIdsRef.current.includes(id)) {
          continue;
        }

        const scheduleType = resolveScheduleType(schedule);
        if (!forceTypes.has(scheduleType as AllPlanScheduleType) && !autoAcceptTypes?.[scheduleType]) {
          continue;
        }
        if (scheduleType === 'meal' && commitMealTypes?.size) {
          const mealType = getMealTypeFromName(schedule.name);
          if (!mealType || !commitMealTypes.has(mealType)) {
            continue;
          }
        }

        if (scheduleType !== 'meal' && scheduleType !== 'sleep' && forceTypes.has(scheduleType as AllPlanScheduleType)) {
          continue;
        }

        try {
          await acceptSchedule(id, schedule, {
            isAutoAccepted: true,
            cacheDate: options?.targetDate,
          });
        } catch {
          // ignore individual auto-accept failures
        }
      }
    },
    [acceptSchedule, autoAcceptTypes, mainCalendar?.calendarId]
  );

  const autoAcceptSchedulesRef = useRef(autoAcceptSchedules);
  useEffect(() => {
    autoAcceptSchedulesRef.current = autoAcceptSchedules;
  }, [autoAcceptSchedules]);

  const runDailyDefaultsPlan = useCallback(
    async ({ todayKey, signature }: { todayKey: string; signature: string }) => {
      if (dailyDefaultsRunPromiseRef.current) {
        return dailyDefaultsRunPromiseRef.current;
      }

      const run = (async () => {
        if (skipAllPlanning || isLoadingRef.current || !mainCalendar?.calendarId || !user?.userId) {
          return;
        }

        const store = readDailyDefaultsStore();
        const todayEvents = cachedEventsRef.current;
        const includeTypes = schedulerPreferences?.allPlan?.includeTypes ?? null;
        const recurringDay = dayjs(todayKey).format('dddd').toLowerCase() as
          | 'monday'
          | 'tuesday'
          | 'wednesday'
          | 'thursday'
          | 'friday'
          | 'saturday'
          | 'sunday';
        const resolveResult = resolveDailyDefaultsTypesToRun({
          todayKey,
          now: dayjs(),
          alreadyRanDate: store.lastDailyDefaultsDate,
          lastPresentKinds: store.lastPresentKinds,
          skipAllPlanning,
          includeTypes,
          disableMealAutoSchedule: !!mealPref.disableAutoSchedule,
          mealLatestTimes: {
            breakfast: mealPref.breakfast?.latestTime,
            lunch: mealPref.lunch?.latestTime,
            dinner: mealPref.dinner?.latestTime,
          },
          mealRecurringEnabled: {
            breakfast: mealPref.breakfast?.recurring?.[recurringDay]?.enabled,
            lunch: mealPref.lunch?.recurring?.[recurringDay]?.enabled,
            dinner: mealPref.dinner?.recurring?.[recurringDay]?.enabled,
          },
          sleepStartHHmm: null,
          undoCounts: store.undoCounts,
          calendarEvents: todayEvents,
          pendingSchedules: schedulesLengthRef.current ? schedulesRef.current : [],
          discardedIds: discardedIdsRef.current,
          plannedTypes: plannedTypesRef.current,
        });

        writeDailyDefaultsStore({
          ...store,
          lastDailyDefaultsDate: todayKey,
          lastPresentKinds: resolveResult.kinds,
        });

        if (!resolveResult.kinds.length) {
          return;
        }

        trackEventMixpanel('daily_defaults_run', {
          typesRequested: resolveResult.planTypes,
          kindsRequested: resolveResult.kinds,
          skipReasons: resolveResult.skipReasons,
        });

        const controller = new AbortController();
        rerunAbortControllerRef.current = controller;
        isLoadingRef.current = true;
        setSuggestAllAIPlanState((prev) => ({
          ...prev,
          isLoading: true,
          statusLines: [],
          error: null,
          targetDate: todayKey,
        }));

        try {
          const result = await runSuggestAllAIPlanRef.current({
            signal: controller.signal,
            planTypes: resolveResult.planTypes,
            focusAreas: buildDailyDefaultsFocusAreas(resolveResult.kinds),
            targetDate: todayKey,
          });
          if (controller.signal.aborted) return;

          // Partition the scoped rerun payload only. Merged live state still has
          // cached todo/routine/workout cards; treating those as leftovers would
          // wipe the open Plan My Day UI until cache rehydrate.
          const partitioned = partitionDailyDefaultsRerunSchedules(
            result.newSchedules,
            todayKey,
            resolveResult.kinds
          );
          const committedSchedules = partitioned.commitEntries.map(({ schedule }) => schedule);
          const committedKinds = partitioned.commitEntries.map(({ kind }) => kind);
          const committedTypes = Array.from(
            new Set(committedSchedules.map(resolveScheduleType).filter(isAllPlanScheduleType))
          );

          if (!committedSchedules.length) {
            return;
          }

          const committedIds = partitioned.commitEntries.map(({ id }) => id);
          const mergedPlannedTypes = Array.from(
            new Set([...plannedTypesRef.current, ...committedTypes])
          );
          setPlannedTypes(mergedPlannedTypes);
          plannedTypesRef.current = mergedPlannedTypes;

          await persistDailyDefaultsPlanCacheForDate(todayKey, {
            schedules: committedSchedules,
            plannedTypes: mergedPlannedTypes,
            summary: result.summary,
            acceptedIds: committedIds,
            autoAcceptedIds: committedIds,
            discardedIds: [],
          });

          setSuggestAllAIPlanState((prev) => ({
            ...prev,
            schedules: filterLiveSchedulesAfterDailyDefaultsRerun(
              prev.schedules,
              partitioned.leftoverIdSet
            ),
            acceptedIds: Array.from(new Set([...prev.acceptedIds, ...committedIds])),
            autoAcceptedIds: Array.from(new Set([...prev.autoAcceptedIds, ...committedIds])),
            discardedIds: prev.discardedIds.filter((id) => !committedIds.includes(id)),
          }));

          await autoAcceptSchedulesRef.current(committedSchedules, controller.signal, {
            forceTypes: ['meal', 'sleep'],
            commitMealTypes: mealTypesForDailyDefaultKinds(resolveResult.kinds),
            targetDate: todayKey,
          });

          const toastMessage = buildDailyDefaultsToastMessage(partitioned.commitEntries);
          if (toastMessage) {
            setEventsSnackbar({ message: toastMessage, type: 'success', icon: 'calendar' });
          }

          trackEventMixpanel('daily_defaults_commit', {
            typesCommitted: committedTypes,
            kindsCommitted: committedKinds,
            signature,
          });
        } catch {
          // Keep lastDailyDefaultsDate so an empty/failed run does not retry today.
        } finally {
          isLoadingRef.current = false;
          setSuggestAllAIPlanState((prev) => ({ ...prev, isLoading: false }));
        }
      })();

      dailyDefaultsRunPromiseRef.current = run.finally(() => {
        dailyDefaultsRunPromiseRef.current = null;
      });
      return dailyDefaultsRunPromiseRef.current;
    },
    [
      mainCalendar?.calendarId,
      mealPref,
      schedulerPreferences?.allPlan?.includeTypes,
      setEventsSnackbar,
      setSuggestAllAIPlanState,
      skipAllPlanning,
      user?.userId,
    ]
  );

  // Restore multi-date Plan My Day cache; optional auto-run on first visit / empty cache.
  useEffect(() => {
    if (skipAllPlanning) return;

    // A refresh mid-generation must resume that job, not paint the cached
    // plan from the run before it. Skip hydrate, daily defaults, and auto-run
    // until useDailyPlanJobRecovery finishes and clears the persisted id.
    const pendingDailyPlanJob = Boolean(getPersistedDailyPlanJob() || findPendingPlanCacheJob());

    const run = async () => {
      const signature = computeTodayCalendarSignature(cachedEventsRef.current);
      const { store } = applyAllPlanHomeRestoreIfNeeded({
        today,
        previousTargetDate: plannedTargetDateRef.current,
        isLoading: isLoadingRef.current,
        deferForPendingJob: pendingDailyPlanJob,
        setPlannedTypes: (types) => {
          setPlannedTypes(types);
          plannedTypesRef.current = types;
        },
        setSuggestAllAIPlanState: setSuggestAllAIPlanStateRef.current,
      });

      if (pendingDailyPlanJob) {
        return;
      }

      // Daily defaults and auto-run both force today (selectedDate + plan target).
      // Skip them while Plan My Day is showing a future date with schedules.
      if (
        shouldSkipAutoRunForActiveFuturePlan({
          today,
          activeTargetDate: plannedTargetDateRef.current,
          isLoading: isLoadingRef.current,
          schedulesLength: schedulesLengthRef.current,
        })
      ) {
        return;
      }

      await runDailyDefaultsPlan({ todayKey: today, signature });

      const autoRun = resolveAllPlanAutoRunDecision({
        store,
        today,
        autoRunOnFirstVisit,
      });
      if (!autoRun.shouldRun || isLoadingRef.current) return;

      const runKey = `${today}|${autoRun.runKind}`;
      if (getLastAutoRunKey() === runKey) return;
      setLastAutoRunKey(runKey);

      const controller = new AbortController();
      autoRunAbortControllerRef.current = controller;

      const currentPlannedTypes = plannedTypesRef.current;
      const skipTypes: AllPlanScheduleType[] = [];
      if (currentPlannedTypes.includes('sleep')) skipTypes.push('sleep');
      if (currentPlannedTypes.includes('meal')) skipTypes.push('meal');

      // Auto-replan always targets today — ignore calendar selected date / future plans.
      const result = await runSuggestAllAIPlanRef.current({
        signal: controller.signal,
        skipTypes,
        targetDate: today,
      });
      if (controller.signal.aborted) return;

      const nextRunTypes: AllPlanScheduleType[] = result.schedules.reduce((acc, s) => {
        const t = resolveScheduleType(s);
        if (isAllPlanScheduleType(t) && !acc.includes(t)) acc.push(t);
        return acc;
      }, [] as AllPlanScheduleType[]);

      const mergedPlannedTypes = Array.from(new Set([...plannedTypesRef.current, ...nextRunTypes]));
      setPlannedTypes((prev) => Array.from(new Set([...prev, ...nextRunTypes])));
      plannedTypesRef.current = mergedPlannedTypes;

      const todayCacheSnapshot = buildPlanCacheSnapshotForDate(result, today);
      if (shouldPersistPlanCacheSnapshot(todayCacheSnapshot)) {
        await persistPlanCacheForDate(
          today,
          buildPlanCacheEntryFromSnapshot({
            snapshot: todayCacheSnapshot,
            plannedTypes: mergedPlannedTypes,
            signature,
            lastRunTodoIds: result.lastRunTodoIds,
            lastRunRoutineIds: result.lastRunRoutineIds,
          }),
          { replacesPendingJobId: result.jobId }
        );
      }

      await autoAcceptSchedulesRef.current(result.newSchedules, controller.signal, {
        targetDate: today,
      });
      openAIChatSidebar();
    };

    run().catch(() => undefined);
  }, [autoRunOnFirstVisit, openAIChatSidebar, runDailyDefaultsPlan, skipAllPlanning, today]);

  const schedulesWithReplannedMetadata = useMemo(
    () => enrichSchedulesWithReplannedMetadata(schedules, cachedEventsForPlanDate, resolveScheduleType),
    [cachedEventsForPlanDate, schedules]
  );

  /** Abort in-flight plan-my-day generation and clear plan Recoil state so
   * New Chat mid-run cannot leave status lines / schedules leaking into the
   * next conversation. */
  const cancelActivePlanGeneration = useCallback(() => {
    // Dropping the stream does not stop science, so cancel the job itself.
    // Read the id before resetAllPlanState clears the persisted record.
    const pendingJobId = getPersistedDailyPlanJob()?.jobId;
    if (pendingJobId) {
      void cancelChatJob(pendingJobId);
    }
    autoRunAbortControllerRef.current?.abort();
    autoRunAbortControllerRef.current = null;
    rerunAbortControllerRef.current?.abort();
    rerunAbortControllerRef.current = null;
    isLoadingRef.current = false;
    resetAllPlanState();
  }, [resetAllPlanState]);

  const rerunSuggestionWithTypes = useCallback(
    async (
      typesToRun: AllPlanScheduleType[],
      selection?: { todos?: TaskItemForSchedule[]; routine?: RoutineActivity[] },
      options?: { focusAreas?: string[]; targetDate?: string }
    ) => {
      if (skipAllPlanning) return;
      trackEventMixpanel('rerun_auto_suggestion', {
        source: 'all_plan',
        selectedTypes: typesToRun,
      });
      const controller = new AbortController();
      rerunAbortControllerRef.current = controller;

      const uniqueTypes = Array.from(new Set(typesToRun.filter(isAllPlanScheduleType)));
      const todayKey = dayjs().format('YYYY-MM-DD');
      const runTargetDate = options?.targetDate || todayKey;

      // Set loading synchronously before any await so the panel's first paint shows the
      // loading hero instead of the empty "no suggestions" state.
      isLoadingRef.current = true;
      setSuggestAllAIPlanState((prev) => ({
        ...prev,
        isLoading: true,
        statusLines: [],
        error: null,
        targetDate: runTargetDate,
      }));

      const result = await runSuggestAllAIPlan({
        signal: controller.signal,
        planTypes: uniqueTypes,
        todos: selection?.todos,
        routine: selection?.routine,
        focusAreas: options?.focusAreas,
        targetDate: runTargetDate,
        replaceTargetDate: true,
      });
      if (controller.signal.aborted) return;

      const nextRunTypes: AllPlanScheduleType[] = result.schedules.reduce((acc, s) => {
        const t = resolveScheduleType(s);
        if (isAllPlanScheduleType(t) && !acc.includes(t)) acc.push(t);
        return acc;
      }, [] as AllPlanScheduleType[]);

      // In-memory plannedTypes feed today's FAB/review — only update for today runs.
      // Future-date runs still persist under their own cache key below so next-morning
      // sleep-sync auto-replan can find the plan once that day becomes today.
      const isTodayRun = shouldUpdateTodayScopedPlanState(runTargetDate);
      const mergedPlannedTypes = isTodayRun
        ? Array.from(new Set([...plannedTypesRef.current, ...nextRunTypes]))
        : nextRunTypes;

      if (isTodayRun) {
        setPlannedTypes(mergedPlannedTypes);
        plannedTypesRef.current = mergedPlannedTypes;
      }

      try {
        const dateCacheSnapshot = buildPlanCacheSnapshotForDate(result, runTargetDate);
        // Don't wipe a good date entry when the run failed or returned nothing for that day.
        if (shouldPersistPlanCacheSnapshot(dateCacheSnapshot)) {
          await persistPlanCacheForDate(
            runTargetDate,
            buildPlanCacheEntryFromSnapshot({
              snapshot: dateCacheSnapshot,
              plannedTypes: mergedPlannedTypes,
              signature: isTodayRun ? computeTodayCalendarSignature(cachedEventsRef.current) : '',
              lastRunTodoIds: result.lastRunTodoIds,
              lastRunRoutineIds: result.lastRunRoutineIds,
            }),
            { replacesPendingJobId: result.jobId }
          );
        }
      } catch {
        // ignore
      }

      await autoAcceptSchedulesRef.current(result.newSchedules, controller.signal, {
        targetDate: runTargetDate,
      });
      openAIChatSidebar();
    },
    [
      openAIChatSidebar,
      runSuggestAllAIPlan,
      setSuggestAllAIPlanState,
      skipAllPlanning,
    ]
  );

  const rerunSuggestion = useCallback(async () => {
    if (skipAllPlanning) return;
    setIsAllPlanPanelOpen(true);
    await rerunSuggestionWithTypes(defaultRerunTypes);
  }, [defaultRerunTypes, rerunSuggestionWithTypes, setIsAllPlanPanelOpen, skipAllPlanning]);

  const rerunAllSuggestions = useCallback(async () => {
    if (skipAllPlanning) return;
    setIsAllPlanPanelOpen(true);
    await rerunSuggestionWithTypes(ALL_PLAN_SCHEDULE_TYPES);
  }, [rerunSuggestionWithTypes, setIsAllPlanPanelOpen, skipAllPlanning]);

  const openAllPlanPanelInChatSidebar = useCallback(
    (options?: { targetDate?: string }) => {
      if (options?.targetDate) {
        setSuggestAllAIPlanState((prev) => ({
          ...prev,
          targetDate: options.targetDate!,
        }));
      }
      setSidebarOpen(true);
      setIsAllPlanPanelOpen(true);
    },
    [setIsAllPlanPanelOpen, setSidebarOpen, setSuggestAllAIPlanState]
  );
  return {
    schedules: schedulesWithReplannedMetadata,
    summary,
    statusLines,
    isLoading,
    skipAllPlanning,
    availablePlanTypes: ALL_PLAN_SCHEDULE_TYPES,
    defaultRerunTypes,
    acceptedIds,
    autoAcceptedIds,
    discardedIds,
    error,
    targetDate: plannedTargetDate,
    acceptSchedule,
    discardSchedule,
    updateScheduleTime,
    resetAllPlanState,
    cancelActivePlanGeneration,
    rerunSuggestion,
    rerunAllSuggestions,
    rerunSuggestionWithTypes,
    openAllPlanPanelInChatSidebar,
  };
};
